import { useMemo } from 'react';
import { useFragment } from '@apollo/client';
import type {
  AccountPlanAndTrialInfoFragment,
  SubscriptionInfoFragment,
} from '@frameio/graphql-client/generated';
import {
  AccountPlanAndTrialInfo,
  SubscriptionInfo,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';

type currentSubscriptionAddOnsTracking = {
  current_seat_add_on_count: number;
  current_storage_add_on_count: number;
  current_mounted_storage_add_on_count: number;
};

const getAddOn = ({
  subscription,
  modifier,
}: {
  subscription: SubscriptionInfoFragment;
  modifier: string;
}) => subscription.addOns?.find((addOn) => addOn.modifier === modifier);

export function useCurrentSubscriptionAddOnsTracking():
  | currentSubscriptionAddOnsTracking
  | undefined {
  const accountId = useActiveAccountId() ?? '';

  /** only required to get the subscription id */
  const {
    complete: accountPlanAndTrialInfoComplete,
    data: accountPlanAndTrialInfoData,
  } = useFragment<AccountPlanAndTrialInfoFragment>({
    fragment: AccountPlanAndTrialInfo,
    fragmentName: 'AccountPlanAndTrialInfo',
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const subscriptionId = accountPlanAndTrialInfoData?.subscription?.id;

  const { complete: subscriptionInfoComplete, data: subscriptionInfoData } =
    useFragment<SubscriptionInfoFragment>({
      fragment: SubscriptionInfo,
      fragmentName: 'SubscriptionInfo',
      from: {
        __typename: 'AccountSubscription',
        id: subscriptionId ?? '',
      },
    });

  const currentSubscriptionAddOnsTracking = useMemo(() => {
    const currentSubscriptionAddOns = subscriptionInfoData?.addOns;

    if (
      !accountPlanAndTrialInfoComplete ||
      !subscriptionInfoComplete ||
      !currentSubscriptionAddOns
    ) {
      return undefined;
    }

    const currentSeatAddOnCount = getAddOn({
      subscription: subscriptionInfoData,
      modifier: 'user_limit',
    })?.quantity;

    const currentStorageAddOnCount = getAddOn({
      subscription: subscriptionInfoData,
      modifier: 'storage_limit',
    })?.quantity;

    const currentMountedStorageAddOnCount = getAddOn({
      subscription: subscriptionInfoData,
      modifier: 'mounted_storage_limit',
    })?.quantity;

    return {
      current_seat_add_on_count: currentSeatAddOnCount ?? 0,
      current_storage_add_on_count: currentStorageAddOnCount ?? 0,
      current_mounted_storage_add_on_count:
        currentMountedStorageAddOnCount ?? 0,
    };
  }, [
    accountPlanAndTrialInfoComplete,
    subscriptionInfoComplete,
    subscriptionInfoData,
  ]);

  return currentSubscriptionAddOnsTracking;
}
